import { useState } from 'react';
import type { FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/da1eg3drlbpoffc5h59g';

const contactInfo = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'support@manwacomic.example' },
  { icon: 'ri-community-line', label: '反馈', value: '欢迎提交追更建议与作品报错' },
  { icon: 'ri-chat-smile-3-line', label: '合作', value: '漫画投稿与版权合作开放中' },
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get('company_alt') as string) || '';
    if (honeypot.trim().length > 0) {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('submitting');
    setFormError('');
    try {
      const payload = new URLSearchParams();
      fd.forEach((value, key) => {
        if (key === 'company_alt') return;
        payload.append(key, String(value));
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const msg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(msg) || msg.includes('form data is spam');

      if (!res.ok || !parsed || parsed.code !== 'OK' || isSpam) {
        setFormError(msg || '提交失败，请稍后重试。');
        setStatus('error');
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setFormError('网络异常，请稍后重试。');
      setStatus('error');
    }
  };

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系漫蛙漫画',
    description: '联系我们反馈追更建议、作品报错与漫画合作。',
  };

  return (
    <section id="contact" className="bg-background-50 py-16 md:py-24">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(contactSchema) }} />
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <span className="inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
              联系我们
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              有任何建议，随时告诉我们
            </h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              无论是追更建议、作品报错，还是漫画合作，都欢迎通过表单与我们联系。
              我们会认真阅读每一条反馈。
            </p>

            <div className="mt-6 space-y-3">
              {contactInfo.map((c) => (
                <div key={c.label} className="flex items-start gap-3 rounded-2xl bg-background-100 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                    <i className={`${c.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-500">{c.label}</div>
                    <div className="mt-0.5 text-sm font-semibold text-foreground-900">{c.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-8 lg:col-span-3">
            <h3 className="font-heading text-lg font-bold text-foreground-950">发送留言</h3>
            <form
              id="contact-form"
              data-readdy-form="漫蛙漫画联系表单"
              onSubmit={handleSubmit}
              className="mt-5 space-y-4"
            >
              <div className="hp-shelter">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    placeholder="怎么称呼你"
                    className="rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="想说的建议、反馈或合作意向……"
                  className="resize-none rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              {status === 'error' && formError && (
                <p className="rounded-lg bg-primary-100 px-4 py-2.5 text-sm text-primary-800">{formError}</p>
              )}
              {status === 'success' && (
                <p className="rounded-lg bg-accent-100 px-4 py-2.5 text-sm text-accent-900">
                  感谢你的留言，我们已收到！
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 sm:w-auto"
              >
                {status === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin" aria-hidden="true"></i> 提交中
                  </>
                ) : (
                  <>
                    提交留言
                    <i className="ri-send-plane-line ml-2" aria-hidden="true"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}